import { DateTime } from 'luxon';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_Entry } from '@cf-shared/data-model/user-log';
import { DayTaskStatuses, TaskStatus } from '@cf-shared/data-model/day-task-status-cache';
import { pickOneMealPerSlot } from '@cf-shared/data-model/log-meal';
import { logEntryHoldsUserData, pickOneEntryPerSingletonSlot } from '@cf-shared/data-model/log-entry-user-data';
import { deepCompareEQ } from '@cf-shared/deep-helpers';
import { Mutex } from '@cf-shared/mutex';

/**
 * Pure logic for the day-task-status cache (#4212).
 *
 * This module holds the calculation behind the calendar day markers (the small
 * circles on the Training / Nutrition / Home calendars). It is kept free of
 * Angular and database dependencies so it can be unit tested — same pattern as
 * `user-db.migrations.ts`. The database plumbing that drives it lives in
 * `day-task-status.service.ts`.
 */

/**
 * How far the calendar's id-range reads pad the requested span, in whole days
 * at each end.
 *
 * WHY THERE IS ANY PADDING. A log entry's document id embeds the entry's time
 * written in UTC (`log/<time in UTC>-<type>`), and it is built once when the
 * entry is first saved. The calendar asks about LOCAL days. A local day is not
 * the same 24 hours as a UTC day, so a read keyed on the local date has to
 * reach into the UTC days on either side or it misses entries near midnight.
 *
 * WHY TWO DAYS AND NOT ONE (#5085). The person reading the calendar can be in
 * any timezone on earth: UTC-12 (Baker Island) through UTC+14 (Line Islands).
 * Those two are 26 hours apart, which is MORE than the 24 hours one day of
 * padding buys. So with one day, an entry written at 00:00 local time in UTC+14
 * — never edited, nothing wrong with it — already falls outside the window
 * computed by a reader who has since travelled to UTC-12, and that day loses
 * its calendar dot. Two days covers the full 26-hour spread and leaves 22 hours
 * of slack at each edge, which also absorbs an entry that was moved across a
 * day boundary.
 *
 * The cost of the extra day is bounded and one-directional: a wider read can
 * only return MORE rows, and every caller filters them back down to the
 * requested dates through {@link groupTaskStatusRowsByDate}, so the extra rows
 * are dropped rather than drawn.
 *
 * MIRRORED VALUE: `tools/src/measure-log-entry-window-5078-shared.ts` measures
 * production data against this same window and holds its own copy of these
 * bounds in hours (`WINDOW_START_HOURS_FROM_MIDNIGHT` /
 * `WINDOW_END_HOURS_FROM_MIDNIGHT`). Change this and change those, or that
 * measurement quietly answers a question about code that no longer exists.
 */
export const TASK_STATUS_READ_PADDING_DAYS = 2;

/**
 * The document-id range a task-status read covers for the local days
 * `[startDate, endDate]`, as the two UTC timestamps that bracket it.
 *
 * Both of the calendar's raw log reads build their range from this, so the
 * padding is decided in exactly one place — see
 * {@link TASK_STATUS_READ_PADDING_DAYS} for why it is the width it is. The
 * caller appends the `log/` prefix (and, for the upper bound of an id range,
 * the `￰` suffix), because the view query and the id-range read wrap these
 * same two timestamps differently.
 *
 * The two edges are deliberately not symmetric. The lower bound is exactly the
 * start of its day, so it is inclusive. The upper bound comes from
 * `endOf('day')`, which is one millisecond short of the next midnight, so an
 * entry landing exactly on the upper edge is outside the read.
 */
export function taskStatusIdRangeBounds(
  startDate: DateTime,
  endDate: DateTime,
): { startUtc: string; endUtc: string } {
  return {
    startUtc: startDate.startOf('day').minus({ days: TASK_STATUS_READ_PADDING_DAYS }).toUTC().toISO()!,
    endUtc: endDate.endOf('day').plus({ days: TASK_STATUS_READ_PADDING_DAYS }).toUTC().toISO()!,
  };
}

/** The entry types that produce a task marker on the calendar. */
export const TASK_STATUS_ENTRY_TYPES: readonly LogRecordType[] = [
  LogRecordType.Workout,
  LogRecordType.Meal,
  LogRecordType.WeighIn,
  LogRecordType.WeeklyCheckup,
];

/** Which read path asked for task statuses — used in telemetry payloads so a
 *  lying/unavailable query can be traced to its origin (#4212). */
export enum TaskStatusReadPhase {
  /** the calendar load path filling missing/stale cache days */
  Load = 'load',
  /** the entry-save update path refreshing the affected days */
  Update = 'update',
  /** the update path's reconcile step detected the view missed a saved entry */
  UpdateReconcile = 'update-reconcile',
}

/** One row as returned by the `logEntriesForTaskStatus` view: the key is the
 *  entry's `time_utc`, the value carries just what the status math needs. */
export interface TaskStatusQueryRow {
  /** The document id. A view row carries it too, but nothing guarantees it. */
  id?: string;
  key: string;
  value: {
    type: LogRecordType;
    state: LogRecordState;
    timeOffset_min: number;
    /** A meal's name, which is what decides its slot (#6281). Only rows built
     *  from the raw documents carry it; the view does not emit it. */
    name?: string;
    /** For a once-a-day entry (not a meal): does the document hold user data, as
     *  `logEntryHoldsUserData` answers it? Worked out from the whole document,
     *  because for a workout the answer depends on its sets, which a row does
     *  not carry. Only rows built from the raw documents carry it; the view
     *  does not emit it. */
    holdsUserData?: boolean;
  };
}

/** One entry of a day, as the status calculation sees it. */
export interface TaskStatusEntry {
  type: LogRecordType;
  state: LogRecordState;
  _id?: string;
  name?: string;
  holdsUserData?: boolean;
  time_utc: string;
  timeOffset_min: number;
}

/** Per-day lists of the entries that drive the status calculation. */
export type TaskEntriesByDate = Map<string, TaskStatusEntry[]>;

/**
 * Group view rows by their LOCAL calendar date, keeping only rows that fall
 * inside the requested [startDate, endDate] range. Each entry's own stored
 * timezone offset decides which local day it belongs to.
 */
export function groupTaskStatusRowsByDate(
  rows: TaskStatusQueryRow[],
  startDate: DateTime,
  endDate: DateTime,
  today: DateTime,
  corruptTimeRowsOut?: TaskStatusQueryRow[],
): TaskEntriesByDate {
  const entriesByDate: TaskEntriesByDate = new Map();

  for (const row of rows) {
    // convert the entry's UTC time to its local time using its stored offset
    const timeOffset = row.value.timeOffset_min || 0;
    const entryLocalTime = getEntryLocalTimeInDesiredTimeZone(
      { time_utc: row.key, timeOffset_min: timeOffset } as Log_Entry,
      today,
    );
    // if the entry's stored time is corrupt, skip it — a null date key must
    // never become a map key. the skip is collected into the out-array so the
    // caller can surface the data-integrity condition (this pure module has no
    // logging dependency; same out-array pattern as rescuedCompletedEntries)
    if (!entryLocalTime.isValid) {
      corruptTimeRowsOut?.push(row);
      continue;
    }
    const dateKey = entryLocalTime.toISODate();

    // if the entry falls outside the requested range, skip it
    if (dateKey < startDate.toISODate() || dateKey > endDate.toISODate()) {
      continue;
    }

    if (!entriesByDate.has(dateKey)) {
      entriesByDate.set(dateKey, []);
    }
    entriesByDate.get(dateKey)!.push({
      type: row.value.type,
      state: row.value.state,
      _id: row.id,
      name: row.value.name,
      holdsUserData: row.value.holdsUserData,
      time_utc: row.key,
      timeOffset_min: timeOffset,
    });
  }

  return entriesByDate;
}

/**
 * Convert full log docs (from a view-independent allDocs read) into the same
 * row shape the view would have emitted. Used as the fallback data source when
 * the view is suspected of lying (#4212 — a wedged local view index returns
 * zero rows without any error).
 */
export function logDocsToTaskStatusRows(docs: Log_Entry[]): TaskStatusQueryRow[] {
  const rows: TaskStatusQueryRow[] = [];
  for (const doc of docs) {
    if (doc == null || doc._deleted) continue;
    if (!TASK_STATUS_ENTRY_TYPES.includes(doc.type)) continue;
    rows.push(logEntryToTaskStatusRow(doc));
  }
  return rows;
}

/**
 * One log entry as a status row. A meal also carries its id and name, so the
 * day's meals can be reduced to the ones the meal list shows (#6281). Any other
 * entry carries whether it holds user data, so the day's once-a-day entries can
 * be reduced the same way the day list reduces them. The view cannot supply
 * those, which is why its rows are counted one by one instead.
 */
function logEntryToTaskStatusRow(entry: Log_Entry): TaskStatusQueryRow {
  const row: TaskStatusQueryRow = {
    id: entry._id,
    key: entry.time_utc,
    value: {
      type: entry.type,
      state: entry.state,
      timeOffset_min: entry.timeOffset_min || 0,
    },
  };
  if (entry.type === LogRecordType.Meal) {
    if (entry.name) row.value.name = entry.name;
  } else {
    row.value.holdsUserData = logEntryHoldsUserData(entry);
  }
  return row;
}

/**
 * Where a task-status read's rows actually came from (#4350).
 *
 * Deliberately NOT {@link TaskStatusViewCheck}, which looks similar and answers a
 * different question. That enum exists to say whether a COUNT needs multiplying by the
 * sample rate; this one says whether the view was consulted at all. Reporting the
 * sampling enum on a site that fires on every save would invite a reader to scale a
 * count that must not be scaled, which is exactly the mistake this split avoids.
 */
export enum TaskStatusRowSource {
  /** The view's answer, with no raw-doc cross-check: a future-only range (the view is
   *  its only data source) or a mid-catch-up read. */
  View = 'view',
  /** Built from the allDocs id-range read; the view was never queried (#4249 Phase 3). */
  RawDocs = 'alldocs',
  /** Built from the raw docs AND cross-checked against the view — the sampled read. */
  RawDocsCheckedAgainstView = 'alldocs-checked-against-view',
}

/** One entry the caller held in hand that the read did not return (#4350). */
export interface MissingSavedEntry {
  /** The document id. It embeds the time the entry was CREATED at. */
  id: string;
  /** The entry's current time. Moving an entry rewrites this and not the id, so the
   *  two disagreeing is itself the finding. */
  time_utc: string;
}

/** How many missing entries a single telemetry report spells out (#4350). One save
 *  can carry a whole day of entries, and a report is not worth an unbounded payload —
 *  the count rides alongside, so a truncated list never reads as the whole story. */
export const MAX_REPORTED_MISSING_ENTRIES = 5;

/**
 * Render one field of the missing entries as a short comma-separated string for a
 * telemetry payload (#4350), capped at {@link MAX_REPORTED_MISSING_ENTRIES}.
 *
 * When the cap bites, the string ends in `+N more` so a reader can see it was cut
 * rather than believing they are looking at every entry.
 */
export function describeMissingSavedEntries(
  entries: MissingSavedEntry[],
  pick: (entry: MissingSavedEntry) => string,
): string {
  const shown = entries.slice(0, MAX_REPORTED_MISSING_ENTRIES).map(pick).join(',');
  const hidden = entries.length - MAX_REPORTED_MISSING_ENTRIES;
  return hidden > 0 ? `${shown},+${hidden} more` : shown;
}

/** Result of checking the view's rows against the raw log docs (#4219). */
export interface ViewRowsReconcileResult {
  /** The rows the status math should use. */
  rows: TaskStatusQueryRow[];
  /** True when the view's answer did not match the raw docs — the local view
   *  index is lying (wedged) and must be reported / healed (#4219). */
  viewDiscrepancy: boolean;
}

/**
 * Decide which rows to trust: the view's answer or the view-independent raw
 * log docs read (`fallbackRows`, from an allDocs id-range read).
 *
 * #4212 only replaced the view's answer when it was COMPLETELY empty. #4219
 * showed a wedged index can also return a PARTIAL answer (only entries indexed
 * before the index broke), which slipped through and poisoned the cache.
 */
export function reconcileViewRowsWithLogDocs(
  viewRows: TaskStatusQueryRow[],
  fallbackRows: TaskStatusQueryRow[],
): ViewRowsReconcileResult {
  // the raw docs are the ground truth, so they are ALWAYS what the status math
  // uses when this reconcile runs. the view's answer is only compared against
  // them to detect a lying index (which the caller reports and heals).
  //
  // compare full row identity — key (time_utc), type, state, and offset. a
  // wedged index does not only omit entries: it can also hold a row with a
  // STALE state (the doc was updated in place after the index broke) or a
  // ghost row for a doc deleted after the break. any difference means the
  // index is lying.
  const rowIdentity = (r: TaskStatusQueryRow) =>
    `${r.key}|${r.value.type}|${r.value.state}|${r.value.timeOffset_min || 0}`;
  const viewIdentities = new Set(viewRows.map(rowIdentity));
  const viewDiscrepancy =
    viewRows.length !== fallbackRows.length ||
    fallbackRows.some(r => !viewIdentities.has(rowIdentity(r)));
  return { rows: fallbackRows, viewDiscrepancy };
}

/**
 * How many post-sync calendar reads share one view cross-check (#4249 Phase 3).
 *
 * Since #4219 the raw log docs are the ground truth for the dots, so the view
 * query no longer decides what the user sees — its only remaining job is to be
 * COMPARED against the raw docs, which is how a wedged (lying) view index gets
 * detected. That comparison is the documented regression detector for the
 * changes-feed / view-index contract (#4343), so it is kept — but it does not
 * need to run on every single read. One read in every `N` runs it.
 *
 * `N = 20` is about 5% of post-sync reads: it removes most of the view-query
 * cost while still firing often enough to notice a wedged index. This is a
 * provisional value, exactly like the Phase 1 debounce was: measure the real
 * post-Phase-3 canary rate in Sentry and adjust.
 *
 * NOTE the old 126-fires-per-day baseline in the PRD cannot be used to predict
 * the new rate. Two separate things changed it: Phase 2 already took the view
 * out of the mid-catch-up display path completely, and only THEN does this
 * sampling divide what is left by `N`.
 */
export const TASK_STATUS_VIEW_SAMPLE_RATE = 20;

/**
 * Decides which post-sync calendar reads also run the view cross-check.
 *
 * Deliberately a counter rather than `Math.random()`. A counter gives an exact
 * one-in-`N` rate with no variance, it always samples the FIRST read of a
 * session (so a device that wedged its index while closed is caught at boot
 * rather than eventually), and it is trivially deterministic in tests. Random
 * sampling would leave a short session with a real chance of never checking at
 * all.
 *
 * State is per instance, and the service holds exactly one instance, so "one in
 * `N`" counts reads across every calendar on screen and every read path (load,
 * save-update, reactive flush) together.
 */
export class TaskStatusViewSampler {
  /** Where the next read sits inside the current cycle of `sampleRate` reads.
   *  Zero means "the next read is the one that runs the cross-check". */
  private readIndexInCycle = 0;

  private readonly sampleRate: number;

  constructor(requestedSampleRate: number = TASK_STATUS_VIEW_SAMPLE_RATE) {
    // if the rate is nonsense, check EVERY read — never check none of them. a
    // sampler that silently stopped firing would retire the #4343 detector with
    // no signal that it had, so this fails towards doing too much work
    this.sampleRate = Number.isFinite(requestedSampleRate) && requestedSampleRate >= 1
      ? requestedSampleRate
      : 1;
  }

  /** Call once per post-sync read. True means "also run the view cross-check". */
  shouldSampleThisRead(): boolean {
    const shouldSample = this.readIndexInCycle === 0;
    this.readIndexInCycle = (this.readIndexInCycle + 1) % this.sampleRate;
    return shouldSample;
  }

  /**
   * Put the sample back so the NEXT read runs the cross-check.
   *
   * Two callers, and both are cases where a sample was spent but no comparison
   * actually happened:
   * - a sampled read that could not complete (the view query or the raw read was
   *   unavailable). Without re-arming, that read burns the sample and the next
   *   `N-1` reads skip the view too — so a device whose database keeps going
   *   unavailable would be checked far less often than `N` suggests, and those
   *   are exactly the devices most likely to have a wedged index.
   * - the local database being replaced (sign-out, user switch, impersonation,
   *   fixture restore). The new database's view index state is unknown, which is
   *   the same situation as a fresh boot — and checking the first read after a
   *   boot is the whole reason this is a counter rather than `Math.random()`.
   */
  rearm(): void {
    this.readIndexInCycle = 0;
  }

  /** The rate this sampler is actually using, for telemetry payloads. */
  get rate(): number {
    return this.sampleRate;
  }
}

/**
 * How sampling affected THIS task-status read's view cross-check (#4249 Phase 3).
 *
 * It rides along in the telemetry payloads so a reader can tell, per event,
 * whether the count needs scaling — the whole reason the canary rates moved.
 * Three states rather than a boolean, because the three read paths genuinely
 * differ and a two-way flag would have to lie about one of them. It is also an
 * enum rather than a bare boolean argument, per CLAUDE.md.
 */
export enum TaskStatusViewCheck {
  /** A post-sync read that won the sample: it queried the view and compared.
   *  Multiply counts carrying this by `sampleRate` to recover the real rate. */
  Sampled = 'sampled',
  /** A post-sync read that lost the sample: it never asked the view anything. */
  Skipped = 'skipped',
  /** A read that is never sampled and always queries — a future-only range (the
   *  view is its only data source) or a mid-catch-up read (its zero-row rescue
   *  would report a lying view on every read if the query were skipped).
   *  Counts carrying this are unchanged by Phase 3 — do NOT scale them. */
  Always = 'always',
}

/** What to do when the view-independent raw-docs read could not run at all. */
export enum UnavailableRawReadOutcome {
  /** Nothing trustworthy to show, and nothing may be persisted (#4212). */
  DoNotPersist = 'do-not-persist',
  /** The view's own rows are worth showing, but must never be persisted. */
  DisplayUnverified = 'display-unverified',
}

/**
 * Decide what a post-sync read should do when the raw-docs read — the
 * view-independent ground truth — could not run.
 *
 * The whole decision is "did we get a view answer we could still show?", so it
 * is kept here as a pure function that the service maps onto telemetry and a
 * return value. It lives in the pure module because getting it wrong is silent
 * and expensive in both directions:
 * - answering `DisplayUnverified` with no rows would let the caller persist an
 *   empty answer nobody verified, which is exactly the #4212 cache poisoning;
 * - answering `DoNotPersist` when the view did return rows would blank a
 *   calendar that had a perfectly displayable (if unverified) answer.
 *
 * The unsampled path has no view answer by construction, so it always lands on
 * `DoNotPersist`.
 */
export function decideUnavailableRawReadOutcome(viewRowCount: number): UnavailableRawReadOutcome {
  return viewRowCount === 0
    ? UnavailableRawReadOutcome.DoNotPersist
    : UnavailableRawReadOutcome.DisplayUnverified;
}

/** What the cache load path is allowed to do this round (#4219). */
export interface CacheLoadPlan {
  /** True when the stored cache version differs from the expected one. */
  versionMismatch: boolean;
  /** True when an outdated cache doc should be wiped and re-versioned now. */
  resetOutdatedCache: boolean;
  /** True when computed statuses may be persisted into the cache doc. */
  allowPersist: boolean;
}

/**
 * Decide whether this cache load may WRITE (wipe an outdated cache doc,
 * persist computed statuses) or must stay display-only.
 *
 * #4219: while the user DB's initial (catch-up) sync is incomplete, the local
 * data can be legitimately missing recent docs, so any statuses computed from
 * it must not be persisted — and the version-bump wipe must not run either,
 * because the in-memory cache doc is a shared singleton: even an unsaved wipe
 * could leak out through a later unrelated save.
 */
export function planCacheLoad(
  cacheVersion: number | undefined,
  expectedVersion: number,
  initialSyncCompleted: boolean,
): CacheLoadPlan {
  const versionMismatch = !cacheVersion || cacheVersion !== expectedVersion;
  return {
    versionMismatch,
    resetOutdatedCache: versionMismatch && initialSyncCompleted,
    allowPersist: initialSyncCompleted,
  };
}

/** Result of reconciling the computed statuses with the just-saved entries. */
export interface ReconciledTaskStatuses {
  /** The per-day statuses to persist into the cache. */
  statuses: Map<string, DayTaskStatuses>;
  /** Dates where the read provably missed an entry we hold in hand —
   *  a signal that the local view index is lying (#4212). */
  suspectDates: string[];
  /** The entries behind those dates (#4350). `suspectDates` alone said only that
   *  SOMETHING on some day went missing, which is not enough to explain a fire —
   *  roughly 600 production reports carried a date and nothing else. */
  missingEntries: MissingSavedEntry[];
}

/**
 * Compute the per-day statuses for the update path: the caller just saved (or
 * deleted) `savedEntries` and asks for fresh statuses for the affected range.
 *
 * The in-hand entries are the freshest truth by definition (they were saved
 * milliseconds ago), so they SUPERSEDE whatever the view returned for the same
 * entry: a view row matching an in-hand entry is replaced by the in-hand
 * state, a row for an in-hand DELETED entry is dropped, and an in-hand entry
 * the view failed to return entirely is merged in and its day flagged as
 * suspect (the wedged-view signal from #4212).
 */
export function computeTaskStatusesWithSavedEntries(
  queryRows: TaskStatusQueryRow[],
  savedEntries: Log_Entry[],
  startDate: DateTime,
  endDate: DateTime,
  today: DateTime,
  corruptTimeRowsOut?: TaskStatusQueryRow[],
): ReconciledTaskStatuses {
  // a row matches a saved entry when it has the same time and type — the view
  // emits time_utc as key
  const matchesEntry = (row: TaskStatusQueryRow, entry: Log_Entry) =>
    row.key === entry.time_utc && row.value.type === entry.type;

  const relevantSaved = savedEntries.filter(e => e != null && TASK_STATUS_ENTRY_TYPES.includes(e.type));

  const suspectDates = new Set<string>();
  const missingEntries: MissingSavedEntry[] = [];
  const inHandRows: TaskStatusQueryRow[] = [];
  for (const entry of relevantSaved) {
    // a deleted entry contributes no row — its matching view row (if any) is
    // dropped below, so a stale view cannot resurrect it
    if (entry._deleted) continue;

    // an in-hand entry always contributes its own fresh state
    inHandRows.push(logEntryToTaskStatusRow(entry));

    // if the view failed to return this entry at all, flag its day as suspect
    const foundInQuery = queryRows.some(row => matchesEntry(row, entry));
    if (!foundInQuery) {
      const entryLocalTime = getEntryLocalTimeInDesiredTimeZone(entry, today);
      const entryDate = entryLocalTime.isValid ? entryLocalTime.toISODate() : null;
      if (entryDate != null && entryDate >= startDate.toISODate() && entryDate <= endDate.toISODate()) {
        suspectDates.add(entryDate);
        // hand the entry itself back, not only its date. an entry with no id is
        // still a missing entry, so it is recorded with an empty one rather than
        // dropped — dropping it would make the count disagree with the dates
        missingEntries.push({ id: entry._id ?? '', time_utc: entry.time_utc });
      }
    }
  }

  // drop every view row that an in-hand entry supersedes (fresh state wins,
  // and a deleted entry's stale row must not survive)
  const keptRows = queryRows.filter(row => !relevantSaved.some(entry => matchesEntry(row, entry)));

  const entriesByDate = groupTaskStatusRowsByDate([...keptRows, ...inHandRows], startDate, endDate, today, corruptTimeRowsOut);
  const statuses = buildDayStatusesForRange(entriesByDate, startDate, endDate, today);
  return { statuses, suspectDates: [...suspectDates].sort(), missingEntries };
}

/**
 * True when a cached day's statuses cannot be correct anymore and the day must
 * be recomputed (#4212 defect D3 — cached values were never revalidated):
 * - any category still `Future` although the day is today or in the past
 *   (a day can only move future → past, so this value is impossible);
 * - any category still `InProgress` although the day is before yesterday
 *   (a workout left half-done stays "in progress" for a grace day, but a
 *   week-old "in progress" is stale — recomputing is cheap and safe);
 * - the day is over, but its value was worked out BEFORE it was over (#4694).
 */
export function mustRecomputeCachedStatus(
  dayStatuses: DayTaskStatuses,
  dateKey: string,
  today: DateTime,
): boolean {
  const allStatuses = [
    dayStatuses.workoutStatus,
    dayStatuses.mealsStatus,
    dayStatuses.weighInStatus,
    dayStatuses.weeklyCheckupStatus,
  ];

  const todayKey = today.toISODate();
  if (dateKey <= todayKey && allStatuses.some(s => s === TaskStatus.Future)) {
    return true;
  }

  const yesterdayKey = today.minus({ days: 1 }).toISODate();
  if (dateKey < yesterdayKey && allStatuses.some(s => s === TaskStatus.InProgress)) {
    return true;
  }

  // The two rules above only catch a value that is IMPOSSIBLE for its date. The
  // value that actually reached a customer was merely WRONG: 4 August was stored
  // as `incomplete` while every entry on it was `Done` (#4694). It got there
  // because it was worked out during 4 August, when those entries were still
  // `Planned` — and `incomplete` on a past day is entirely possible, since the
  // user really may have skipped that day, so nothing ever re-checked it.
  //
  // So a day that is over is re-checked once when its value was worked out no
  // later than the day itself. That single recompute stamps a later date, which
  // is what makes this settle instead of recomputing the same day on every load.
  //
  // Deliberately NOT re-checked:
  // - a day with no stamp. The in-memory display memo carries none by design, and
  //   an older app version wrote none, so "no stamp" has to mean "leave it alone"
  //   — see the field's own comment in `day-task-status-cache.ts`.
  // - today, and any future day. Today's value is SUPPOSED to have been worked
  //   out today; re-checking it would churn the cache all day for nothing.
  if (dayStatuses.computedOn != null && dateKey < todayKey && dayStatuses.computedOn <= dateKey) {
    return true;
  }

  return false;
}

/**
 * Calculate the status of one task category (workout / meals / weigh-in /
 * weekly checkup) for one day, from that day's entries of that category.
 */
export function calculateSingleCategoryStatus(
  entries: Array<{ type: LogRecordType; state: LogRecordState }>,
  currentDate: DateTime,
  today: DateTime,
): TaskStatus {
  // if the day is in the future, no status applies yet
  if (currentDate > today) {
    return TaskStatus.Future;
  }

  // if a past or present day has no entries, there was no task that day
  if (entries.length === 0) {
    return TaskStatus.NoTask;
  }

  const allCompleted = entries.every(entry =>
    entry.state === LogRecordState.Done || entry.state === LogRecordState.Skipped);
  const allSkipped = entries.every(entry =>
    entry.state === LogRecordState.Skipped);
  const anyInProgress = entries.some(entry =>
    entry.state === LogRecordState.Incomplete);

  // the skipped test has to come FIRST, because it is the narrower of the two:
  // `allCompleted` counts a Skipped entry as completed, so every all-skipped day
  // also satisfies it. Testing `allCompleted` first therefore made this branch
  // unreachable, and a day the user skipped was drawn with the same tick as a
  // day they trained (#4561). Mixed Done-and-Skipped days are unaffected: they
  // fail this test and still land on `Completed`, so skipping one of three meals
  // does not erase a day the user otherwise finished.
  if (allSkipped) {
    return TaskStatus.NoTask;
  } else if (allCompleted) {
    return TaskStatus.Completed;
  } else if (anyInProgress) {
    return TaskStatus.InProgress;
  } else {
    return TaskStatus.Incomplete;
  }
}

/**
 * Reduce one day's meal entries to the meals its meal list shows (#6281).
 *
 * A day can hold two stored meals under one name, for example an empty planned
 * copy beside the finished meal. The meal list and the day total show one per
 * name, chosen by the shared `pickOneMealPerSlot`; counting the hidden copy too
 * drew "half done" under a day whose meal list shows every meal finished.
 *
 * Only entries that carry a name take part. The view's rows carry none, and an
 * entry with no name has no slot to share, so those are all counted, as before.
 */
function mealsTheMealListShows(mealEntries: TaskStatusEntry[], day: DateTime): TaskStatusEntry[] {
  // a named entry comes from a raw document and so has an id; the rule only
  // uses the id to break a tie between two meals at the same minute
  const named = mealEntries.filter(e => !!e.name).map(e => ({ ...e, _id: e._id ?? '' }));
  const unnamed = mealEntries.filter(e => !e.name);
  return [...pickOneMealPerSlot(named, day).kept, ...unnamed];
}

/**
 * Reduce one day's entries of a once-a-day type (workout, weigh-in, weekly
 * check-up) to the one the day list shows.
 *
 * A day can hold two stored entries of one such type, for example a finished
 * workout beside an empty planned copy. The day list shows one, chosen by the
 * shared `pickOneEntryPerSingletonSlot`; counting the hidden copy too drew
 * "half done" under a day whose list shows the task finished.
 *
 * Only entries that say whether they hold user data take part. The view's rows
 * do not say, so those are all counted, as before.
 */
function onceADayEntriesTheDayListShows(entries: TaskStatusEntry[], day: DateTime): TaskStatusEntry[] {
  // an entry that says whether it holds data comes from a raw document and so has
  // an id; the rule only uses the id to break a tie between two at the same minute
  const judged = entries.filter(e => e.holdsUserData !== undefined).map(e => ({ ...e, _id: e._id ?? '' }));
  const unjudged = entries.filter(e => e.holdsUserData === undefined);
  return [...pickOneEntryPerSingletonSlot(judged, day, e => e.holdsUserData === true).kept, ...unjudged];
}

/**
 * Build the per-day `DayTaskStatuses` for every day in [startDate, endDate]
 * from the grouped entries.
 */
export function buildDayStatusesForRange(
  entriesByDate: TaskEntriesByDate,
  startDate: DateTime,
  endDate: DateTime,
  today: DateTime,
): Map<string, DayTaskStatuses> {
  const taskStatuses = new Map<string, DayTaskStatuses>();

  let currentDate = startDate.startOf('day');
  while (currentDate <= endDate) {
    const dateKey = currentDate.toISODate();
    const dayEntries = entriesByDate.get(dateKey) || [];

    const workoutEntries = onceADayEntriesTheDayListShows(dayEntries.filter(e => e.type === LogRecordType.Workout), currentDate);
    const mealEntries = mealsTheMealListShows(dayEntries.filter(e => e.type === LogRecordType.Meal), currentDate);
    const weighInEntries = onceADayEntriesTheDayListShows(dayEntries.filter(e => e.type === LogRecordType.WeighIn), currentDate);
    const weeklyCheckupEntries = onceADayEntriesTheDayListShows(dayEntries.filter(e => e.type === LogRecordType.WeeklyCheckup), currentDate);

    const dayStatuses: DayTaskStatuses = {
      workoutStatus: calculateSingleCategoryStatus(workoutEntries, currentDate, today),
      mealsStatus: calculateSingleCategoryStatus(mealEntries, currentDate, today),
      weighInStatus: calculateSingleCategoryStatus(weighInEntries, currentDate, today),
      weeklyCheckupStatus: calculateSingleCategoryStatus(weeklyCheckupEntries, currentDate, today),
    };

    taskStatuses.set(dateKey, dayStatuses);
    currentDate = currentDate.plus({ days: 1 });
  }

  return taskStatuses;
}

/** Where a calendar day's status came from on one load (#4249 Phase 2). */
export enum DayStatusSource {
  /** the in-memory display-only memo, which the reactive flush keeps fresh */
  Memo = 'memo',
  /** the persisted cache document */
  Cache = 'cache',
  /** neither had a usable value — the caller must compute this day */
  Missing = 'missing',
}

/**
 * Decide where ONE calendar day's status should come from on this load.
 *
 * The interesting rule is the first one: while the catch-up sync is still
 * running, a memo value BEATS the persisted cache value. That looks backwards —
 * the cache doc is the durable store — but during catch-up it is the stale one.
 * The memo is what the reactive flush updates as documents arrive, while the
 * cache doc still holds whatever was computed before this sync started and is
 * deliberately not written during catch-up (#4219/#4212). Serving the cache
 * first, as the load path did before Phase 2, means every device that already
 * has a populated cache document shows the OLD dots for the whole sync and
 * never sees a single reactive update.
 *
 * Once the sync has completed the ordering flips back: the cache doc is
 * authoritative again, it is being persisted again, and the memo is finished.
 */
export function chooseDayStatusSource(
  cachedStatuses: DayTaskStatuses | undefined,
  memoStatuses: DayTaskStatuses | undefined,
  dateKey: string,
  today: DateTime,
  initialSyncCompleted: boolean,
): DayStatusSource {
  // if the catch-up sync is still running, the memo is the fresher of the two —
  // but it is still subject to the same #4212 staleness rule as the cache. A
  // memo entry computed before local midnight records the day as `Future`, and
  // serving that for a day now in the past is the exact D3 shape the cache
  // branch guards against; a long catch-up can easily cross midnight.
  if (!initialSyncCompleted
    && memoStatuses !== undefined
    && !mustRecomputeCachedStatus(memoStatuses, dateKey, today)) {
    return DayStatusSource.Memo;
  }
  // a cached value that can no longer be correct (e.g. still "future" for a
  // past day) counts as missing and is recomputed (#4212 defect D3)
  if (cachedStatuses !== undefined && !mustRecomputeCachedStatus(cachedStatuses, dateKey, today)) {
    return DayStatusSource.Cache;
  }
  return DayStatusSource.Missing;
}

/**
 * Write freshly computed statuses into the memo, but NEVER let a day go
 * backwards to "nothing here" while the catch-up sync is still running.
 *
 * Mid-sync, a day with no local entries is ambiguous: it can mean the user did
 * nothing that day, or it can mean that day's documents have not arrived yet.
 * The two are indistinguishable from local data alone, and guessing wrong in the
 * "already displayed something" direction is the visible one — a dot that was
 * showing a real workout blanks out and comes back minutes later. This is the
 * same reasoning as #4212's zero-row rule, applied to the memo.
 *
 * So an empty computed value for a day the memo already has a NON-empty value
 * for is dropped. Everything else is written through. Returns true when any day
 * actually changed, which the caller uses as its publish gate — publishing an
 * update for a recompute that matched would reload every open calendar for
 * nothing.
 *
 * This is the ONLY way the memo is written. A plain downgrading variant existed
 * alongside it briefly; it turned out to have no caller, because the memo is
 * only ever written while the sync is incomplete, and mid-sync an empty answer
 * is never trustworthy.
 *
 * The trade this accepts, deliberately: a day whose entries are still arriving
 * can show a PARTIAL value for a while. That is bounded by the post-sync
 * one-shot, which recomputes every visible day from complete data and persists.
 */
export function applyStatusesToMemoWithoutDowngrade(
  memo: Map<string, DayTaskStatuses>,
  statuses: ReadonlyMap<string, DayTaskStatuses>,
): boolean {
  let changed = false;
  for (const [dateKey, dayStatuses] of statuses) {
    const previous = memo.get(dateKey);
    // if this day computed to nothing but already showed something, keep what
    // is showing — mid-sync an empty answer means "not arrived yet"
    if (previous !== undefined && isEmptyDayStatuses(dayStatuses) && !isEmptyDayStatuses(previous)) {
      continue;
    }
    if (previous === undefined || !deepCompareEQ(previous, dayStatuses)) {
      changed = true;
    }
    memo.set(dateKey, dayStatuses);
  }
  return changed;
}

/**
 * True when a day's statuses say "there is nothing on this day" in every
 * category. Used by the never-downgrade rule above, so it must agree with what
 * the calendar renders as an empty day.
 */
export function isEmptyDayStatuses(statuses: DayTaskStatuses | undefined): boolean {
  if (statuses === undefined) return true;
  // named explicitly rather than iterating the object's values, so a reader can
  // see exactly which categories decide "empty". NOTE this is not enforced:
  // DayTaskStatuses is a plain interface, so adding a fifth category compiles
  // fine and this function would silently keep its old meaning — whoever adds
  // one has to remember this line
  const isNothing = (value: TaskStatus | undefined) => value === undefined || value === TaskStatus.NoTask;
  return isNothing(statuses.workoutStatus)
    && isNothing(statuses.mealsStatus)
    && isNothing(statuses.weighInStatus)
    && isNothing(statuses.weeklyCheckupStatus);
}

/*
 * REMOVED in #4249 Phase 2: `DisplayOnlyRevalidationGuard`.
 *
 * It capped a self-triggering background re-check that froze the app (#4243).
 * Phase 2 removed the cycle instead of capping it, so there is nothing left to
 * guard. The full story lives in `design-docs/sync-integrity.md`; the tests in
 * `user-db.task-status.test.ts` still reproduce the old loop to prove it cannot
 * come back.
 */

/* ------------------------------------------------------------------------- *
 * #4249 — reactive per-day status updates
 * ------------------------------------------------------------------------- */

/**
 * Timing and volume tunables for the reactive path.
 *
 * These are informed starting values, NOT settled ones. They are validated by
 * the Phase 1 measurement (PRD step 1.7) and revalidated under catch-up load
 * (step 2.6). Keep them defined here, once, so both the service and the tests
 * read the same numbers.
 */
/** Wait this long after the last pulled doc before flushing, so one sync batch
 *  produces one recompute instead of one per document.
 *
 *  Measured, not guessed (#4249 step 1.7). This started at 300 ms, which turned
 *  out to be SHORTER than the gap between consecutive deliveries from the
 *  fast-lane realtime listener (~350-400 ms apart in a local burst). The result
 *  was that nothing ever coalesced: a 20-day burst produced 19 separate
 *  single-day flushes, and so 19 cache-doc writes, over 8 seconds. At 1000 ms
 *  those same arrivals fall inside one window. The cost is that a single
 *  cross-device change takes up to a second longer to appear, which is
 *  invisible for a background sync update. */
export const REACTIVE_STATUS_DEBOUNCE_MS = 1000;
/** Flush at least this often while docs keep arriving. Without this cap a
 *  sustained pull stream would keep pushing the debounce out and never flush. */
export const REACTIVE_STATUS_HARD_CAP_MS = 2000;
/** Above this many affected days in one flush, stop recomputing day by day and
 *  fall back to invalidating the whole range in one go (the warm-resume burst
 *  case: thousands of docs arrive with the initial sync already complete). */
export const REACTIVE_STATUS_MAX_DATES_PER_FLUSH = 14;
/**
 * The shortest gap allowed between two bounded fallbacks in a row (#4406).
 *
 * A bounded fallback only gets CHEAPER the longer it waits: it does no per-day
 * recompute at all — it deletes the days from the cache document and publishes
 * once — so days arriving during the wait merge into the very same single
 * delete-and-publish. (It DOES carry a cluster list since #4451, but only to
 * publish it; nothing reads those clusters back, so they add no per-day work.)
 * Holding is therefore pure win while a stream keeps running — the opposite of
 * the ordinary flush, where waiting means more days to recompute. Without a
 * limit a backlog drain fired it 218 times in seven minutes; 30 seconds turns
 * that into about 14.
 *
 * Two costs, and the second is the one that is easy to miss. A held wave holds
 * EVERY day in it, including today, so a change made on another device during a
 * drain appears when the fallback runs rather than a couple of seconds later.
 * And the held set lives only in memory: if the process ends before it flushes
 * (a suspended app that is then terminated, a tab close, a crash) those days are
 * never dropped from the cache document — and a day already present there is
 * never recomputed on its own, so its dot stays wrong until something else
 * changes that day. This change widens that window from about 2 seconds to about
 * 32; the app-resume flush covers the suspend case but not termination.
 *
 * Provisional, exactly like the four tunables above. The measurement, the
 * arithmetic behind 30 seconds, and the rest of the reasoning are in
 * `design-docs/sync-integrity.md`.
 */
export const REACTIVE_STATUS_MIN_FALLBACK_INTERVAL_MS = 30 * 1000;

// The catch-up sync has a different rhythm from steady state, so its
// display-only recomputes get their own two constants (#4249 Phase 2).
//
// Steady state coalesces a handful of cross-device edits, and 1000 ms was
// measured as the window that actually collapses a burst. During catch-up,
// documents arrive continuously in 200-document replication batches for
// minutes, so there is no quiet moment to wait for — the debounce is only there
// to stop one batch producing several recomputes, and the hard cap is what
// actually paces the updates the user sees.
//
// These are the PRD's proposed values and they are provisional: Phase 2's STOP
// step re-measures them on a large-account cold start, exactly as the
// steady-state pair was measured in Phase 1.
export const REACTIVE_STATUS_CATCHUP_DEBOUNCE_MS = 500;
export const REACTIVE_STATUS_CATCHUP_HARD_CAP_MS = 2000;
/** A single recompute reads one contiguous id range, so an old edited entry
 *  must not stretch one read across months. Dates further apart than this start
 *  a new cluster. */
export const REACTIVE_STATUS_CLUSTER_MAX_SPAN_DAYS = 35;
/** Longest a failed flush may wait before retrying. A flush that cannot be
 *  trusted (offline, database recovering, a wedged view answering unverified)
 *  puts its dates back and retries — but retrying every second forever would be
 *  the same unbounded loop #4243 was about, with a telemetry event each time.
 *  Each consecutive failure doubles the wait up to this ceiling. */
export const REACTIVE_STATUS_MAX_RETRY_BACKOFF_MS = 5 * 60 * 1000;

/**
 * How long to wait before retrying after `consecutiveFailures` failed flushes.
 * Doubles each time, capped. Called with 1 for the first failure.
 */
export function reactiveStatusRetryDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return REACTIVE_STATUS_HARD_CAP_MS;
  const doubled = REACTIVE_STATUS_HARD_CAP_MS * Math.pow(2, consecutiveFailures - 1);
  return Math.min(doubled, REACTIVE_STATUS_MAX_RETRY_BACKOFF_MS);
}

/**
 * True when a `TaskStatusCacheUpdatedEvent` range touches the days a screen is
 * actually showing.
 *
 * Subscribers reload their WHOLE visible range on the event and ignore its
 * payload, which is fine when the event is about days they show. During a
 * catch-up or backlog sync it often is not: a burst backfilling 2024 log
 * entries publishes a 2024 range, and a calendar showing the last month would
 * still throw away its state and reload for it. On a large account that is
 * dozens of pointless reloads — and each one used to reset the horizontal
 * strip's scroll position back to the selected day.
 *
 * All four bounds are `YYYY-MM-DD` keys, so plain string comparison is correct.
 * A missing bound means "unknown range", which is treated as overlapping —
 * reloading needlessly is safe, skipping a real change is not.
 */
export function taskStatusRangeAffectsVisibleDays(
  eventStart: string | null | undefined,
  eventEnd: string | null | undefined,
  visibleStart: string,
  visibleEnd: string,
): boolean {
  if (!eventStart || !eventEnd) return true;
  return eventStart <= visibleEnd && eventEnd >= visibleStart;
}

/** What a subscriber receives on `TaskStatusCacheUpdatedEvent`. `startDate` and
 *  `endDate` bound every changed day; `clusters` — when the publisher has it —
 *  says where inside that span the changed days actually are. */
export interface TaskStatusChangeRange {
  startDate?: string | null;
  endDate?: string | null;
  clusters?: ReactiveDayStatusCluster[];
}

/**
 * True when a `TaskStatusCacheUpdatedEvent` touches the days a screen is showing.
 *
 * This is the check subscribers should call. `taskStatusRangeAffectsVisibleDays`
 * above is its primitive, and it does most of the work: a sync wave lying
 * entirely in old history fails the overlap test outright, and on a measured
 * device drain that alone suppressed 10 of 14 events for a month-wide window.
 *
 * What it cannot do is judge a wave whose changed days STRADDLE the visible
 * window with none inside it (#4451). The bounding span runs from the earliest
 * changed day to the latest, so a span like `2021-01-04..2026-10-02` covers a
 * mid-2026 window without a single changed day in it, and the primitive can
 * only answer "reload".
 *
 * `clusters` is what fixes that. It names the actual runs of changed days inside
 * the span, so a page can ask about each one separately. Their union is by
 * construction the union of the changed days, so testing them can never miss a
 * change the bounding span would have caught.
 *
 * A publisher that has no cluster list omits it, and then this falls back to the
 * bounding span — the local-save path does exactly that, and its span already
 * describes one just-saved entry. An EMPTY list is treated the same way, as
 * "unknown", never as "nothing changed": a decision that has dates always
 * produces at least one cluster, so an empty list can only mean the publisher
 * did not supply one. Reloading needlessly is safe; skipping a real change is
 * not.
 */
export function taskStatusChangeAffectsVisibleDays(
  change: TaskStatusChangeRange | null | undefined,
  visibleStart: string,
  visibleEnd: string,
): boolean {
  if (change == null) return true;
  const clusters = change.clusters;
  if (clusters == null || clusters.length === 0) {
    return taskStatusRangeAffectsVisibleDays(change.startDate, change.endDate, visibleStart, visibleEnd);
  }
  return clusters.some(cluster =>
    taskStatusRangeAffectsVisibleDays(cluster.startDate, cluster.endDate, visibleStart, visibleEnd));
}

/**
 * The day spans a `TaskStatusCacheUpdatedEvent` says have changed, for a page
 * that keeps its own day-status map and has to drop the changed days from it.
 *
 * Returns `null` for "unknown", which the caller must treat as "drop
 * everything" — anything narrower would leave a stale value behind.
 *
 * Prefers the clusters over the bounding span for the same reason the filter
 * above does: a sync-path span can cover years, and dropping all of it would
 * throw away thousands of cached days to re-read a handful.
 */
export function taskStatusChangedSpans(
  change: TaskStatusChangeRange | null | undefined,
): ReactiveDayStatusCluster[] | null {
  if (change == null) return null;
  if (change.clusters != null && change.clusters.length > 0) return change.clusters;
  if (change.startDate && change.endDate) {
    return [{ startDate: change.startDate, endDate: change.endDate }];
  }
  return null;
}

/**
 * Grow an armed day span so it also covers `startDate..endDate`, keeping the
 * earliest start and the latest end.
 *
 * This exists for the post-sync calendar refresh, which is armed by whichever
 * degraded page load happens first and then waits, possibly for a long time,
 * while the catch-up sync retries. Every arming after the first describes a
 * different page's visible days, and subscribers SKIP a reload whose span misses
 * the days they are showing (`taskStatusChangeAffectsVisibleDays`). So the span
 * that eventually gets published has to be the union of all of them, or a page
 * opened during the wait is never told to reload.
 *
 * ISO date strings (`2026-08-14`) sort in date order as plain text, which is the
 * same fact `taskStatusRangeAffectsVisibleDays` above already relies on, so the
 * comparison needs no date parsing.
 *
 * A missing date is dropped rather than treated as unbounded: it carries no
 * information, and letting it widen the span to "everything" would undo the
 * filtering these spans exist for.
 */
export function widenTaskStatusRange(
  current: { startDate: string; endDate: string } | null,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): { startDate: string; endDate: string } | null {
  if (!startDate || !endDate) return current;
  if (current == null) return { startDate, endDate };
  return {
    startDate: startDate < current.startDate ? startDate : current.startDate,
    endDate: endDate > current.endDate ? endDate : current.endDate,
  };
}

/** What the caller should do with the coalescer's pending dates right now. */
export enum ReactiveDayStatusAction {
  /** Nothing to do: no pending dates, or the wait window has not elapsed. */
  None = 'none',
  /** The catch-up sync is still running and there is nothing new to show — keep
   *  the dates for the post-sync persist, do nothing now. */
  Hold = 'hold',
  /** The catch-up sync is still running: recompute these days for DISPLAY only,
   *  from local data, and write them to the memo. Never persisted (I1). The
   *  post-sync persist list is untouched by this. */
  DisplayOnly = 'display-only',
  /** Recompute and persist these clusters of days. */
  Flush = 'flush',
  /** Too many days at once — drop the range from the cache and publish once. */
  FallbackRange = 'fallback-range',
}

/** One contiguous run of changed days. */
export interface ReactiveDayStatusCluster {
  startDate: string;
  endDate: string;
}

/** The coalescer's answer to "what should I do now?". */
export interface ReactiveDayStatusDecision {
  action: ReactiveDayStatusAction;
  /**
   * The runs of changed days this decision covers, each no wider than
   * `REACTIVE_STATUS_CLUSTER_MAX_SPAN_DAYS`. Their union is always the union of
   * `dates`. Set for every acting decision, but used for two different jobs:
   *
   * - `Flush` and `DisplayOnly` RECOMPUTE them, one database read per cluster.
   * - `FallbackRange` only PUBLISHES them (#4451). It recomputes nothing; the
   *   clusters go on the update event so a page can tell whether any changed
   *   day is on screen. Without them the event says only `rangeStart..rangeEnd`,
   *   which on a backlog drain can straddle a page's visible days with none of
   *   the changed ones inside.
   */
  clusters: ReactiveDayStatusCluster[];
  /** Set for `FallbackRange`: the exact date keys to drop from the cache doc. */
  dates: string[];
  /** Set for `FallbackRange`: the span to publish one update event for. */
  rangeStart: string | null;
  rangeEnd: string | null;
}

const EMPTY_DECISION: ReactiveDayStatusDecision = {
  action: ReactiveDayStatusAction.None,
  clusters: [],
  dates: [],
  rangeStart: null,
  rangeEnd: null,
};

/** The shape the reactive hook hands over: a pulled log doc, which may be a
 *  full body or a body-less deletion tombstone (`{_id, _rev, _deleted}`). */
export type PulledLogDoc = Partial<Log_Entry> & { _id: string; _deleted?: boolean };

/**
 * Pull the timestamp out of a log doc id (`log/<time_utc>-<Type>`).
 *
 * The timestamp is USUALLY `...Z`, but not always: ids written by older app
 * versions carry a zone offset instead (a real one seen on device:
 * `log/2025-09-14T14:59:55.567+02:00-FitnessPlan`). Accepting only `Z` made
 * those ids unparseable, so a pulled entry of a marker type would have had its
 * day silently skipped — the very stale-dot bug this path exists to fix. Both
 * forms are accepted, and `DateTime.fromISO` handles either.
 */
const LOG_ID_UTC_PATTERN = /^log\/(\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2}))/;

/**
 * Read the entry type off a log doc id. Log ids are `log/<time_utc>-<Type>`.
 *
 * The type is found by skipping the timestamp first, NOT by taking whatever
 * follows the last dash: the timestamp contains dashes of its own, so on an id
 * with no type suffix (`log/2026-07-17T14:00:00.000Z`) a last-dash split
 * returns `17T14:00:00.000Z`. That looks like an unknown type, and the caller
 * would drop the doc — silently discarding a real deletion, which is exactly
 * the stale-dot bug this whole path exists to fix.
 *
 * Returns null when the id carries no type suffix, which the caller must treat
 * as "unknown type" (keep the doc), never as "not a marker type".
 */
function typeFromLogDocId(docId: string): string | null {
  const match = LOG_ID_UTC_PATTERN.exec(docId);
  if (match == null) return null;
  const afterTimestamp = docId.slice(match[0].length);
  if (!afterTimestamp.startsWith('-')) return null;
  const type = afterTimestamp.slice(1);
  return type.length > 0 ? type : null;
}

/**
 * Map pulled log docs to the LOCAL calendar dates whose dots they affect.
 *
 * Two cases:
 * - A doc with a usable body uses its own stored timezone offset, exactly like
 *   every other status path (`getEntryLocalTimeInDesiredTimeZone`). Docs of a
 *   type that produces no calendar marker are dropped here.
 * - A deletion tombstone pulled from another device carries no body at all — no
 *   `type`, no `time_utc`, no `timeOffset_min`. All we have is the id, and the
 *   id's timestamp is UTC. Mapping that to one local day would be the naive
 *   guess risk R3 warns about, because the entry's real local day depends on the
 *   offset it was written with. So we return the three local dates around it
 *   instead: any offset on earth is within ±14 hours of UTC, so the real day is
 *   always one of those three. Recomputing two extra days is cheap and correct.
 *
 * Returns a sorted, de-duplicated list of `YYYY-MM-DD` keys.
 *
 * `undecodableIdsOut` collects ids this function could not map to any day at
 * all. That is not a normal outcome: every doc here reached us because the sync
 * layer decided it was a real remote change, and for a body-less tombstone this
 * is the ONLY chance to learn which day to recompute — so a dropped one leaves a
 * permanently stale dot. This module has no logging dependency, so the ids are
 * handed back for the caller to surface (same out-array pattern as
 * `corruptTimeRowsOut`).
 */
export function deriveAffectedDatesFromPulledDocs(
  docs: PulledLogDoc[],
  today: DateTime,
  undecodableIdsOut?: string[],
): string[] {
  const dates = new Set<string>();

  for (const doc of docs) {
    if (doc == null || !doc._id) continue;

    // if the doc has a body we can trust, use the entry's own local day
    const hasUsableBody = doc.type != null && doc.time_utc != null;
    if (hasUsableBody) {
      // if this type produces no calendar marker, it cannot change any dot
      if (!TASK_STATUS_ENTRY_TYPES.includes(doc.type)) continue;
      const localTime = getEntryLocalTimeInDesiredTimeZone(doc as Log_Entry, today);
      // if the stored time is corrupt, fall through to the id-derived window
      // rather than dropping the change silently
      if (localTime.isValid) {
        dates.add(localTime.toISODate());
        continue;
      }
    }

    // body-less doc (a pulled tombstone), or one whose stored time is corrupt.
    // the id still names the type, so a deletion of an entry that draws no
    // calendar marker can be dropped here instead of costing three recomputes.
    const idType = typeFromLogDocId(doc._id);
    if (idType != null && !TASK_STATUS_ENTRY_TYPES.includes(idType as LogRecordType)) continue;

    // derive the conservative 3-day window around the id's UTC day
    const match = LOG_ID_UTC_PATTERN.exec(doc._id);
    const utcDay = match == null ? null : DateTime.fromISO(match[1], { zone: 'utc' });
    if (utcDay == null || !utcDay.isValid) {
      // we know something changed but not which day — must reach a debugger
      undecodableIdsOut?.push(doc._id);
      continue;
    }
    for (const offsetDays of [-1, 0, 1]) {
      dates.add(utcDay.plus({ days: offsetDays }).toISODate());
    }
  }

  return [...dates].sort();
}

/**
 * Group sorted date keys into contiguous clusters, so nearby days share one
 * recompute read while far-apart days stay separate. A cluster never spans more
 * than `REACTIVE_STATUS_CLUSTER_MAX_SPAN_DAYS` days.
 */
export function clusterAffectedDates(sortedDates: string[]): ReactiveDayStatusCluster[] {
  const clusters: ReactiveDayStatusCluster[] = [];

  for (const dateKey of sortedDates) {
    const current = clusters[clusters.length - 1];
    // if this date still fits inside the open cluster's span, extend it
    if (current != null) {
      const spanDays = DateTime.fromISO(dateKey).diff(DateTime.fromISO(current.startDate), 'days').days;
      if (spanDays < REACTIVE_STATUS_CLUSTER_MAX_SPAN_DAYS) {
        current.endDate = dateKey;
        continue;
      }
    }
    clusters.push({ startDate: dateKey, endDate: dateKey });
  }

  return clusters;
}

/**
 * Collects the calendar days that pulled log docs changed, and decides when to
 * recompute them (#4249).
 *
 * Why this exists. Until now nothing reacted to a log doc PULLED from another
 * device, so a workout completed on the user's phone never updated the dot on
 * their tablet — the stale cached value was served forever. The fix is to react
 * to the pulled doc, but reacting per document would be its own disaster: a sync
 * burst delivers thousands of docs, and the #4243 freeze taught us exactly how
 * badly that path punishes per-document work.
 *
 * So this class sits between the two. It gathers affected DATES (never document
 * bodies — two revisions of one doc in a burst collapse to one date, and a
 * stale revision can never win over the verified re-read), waits out a short
 * debounce so one batch becomes one recompute, and hard-caps that wait so a
 * sustained stream still flushes regularly. Past a volume threshold it stops
 * recomputing day by day and asks the caller to invalidate the whole range once
 * instead.
 *
 * This class is pure: it owns the state machine and returns decisions. The
 * caller owns the timers, the database reads, and the cache writes.
 */
export class ReactiveDayStatusCoalescer {
  // Two sets, and the reason is the whole shape of Phase 2.
  //
  // `pending` is the PERSIST list. During the catch-up sync nothing computed may
  // be written to the cache doc (I1), so these dates accumulate untouched until
  // the sync completes and one flush recomputes and persists them all.
  //
  // `pendingDisplay` is the DISPLAY list. It is consumed on every catch-up tick,
  // so each tick only recomputes what arrived since the last one.
  //
  // One set cannot do both jobs. Consuming it for a display tick would throw
  // away the persist list, leaving those days stale forever — a day already
  // present in the cache doc is never recomputed on its own. Not consuming it
  // would make every tick recompute every date collected so far, which over a
  // 16,000-document cold start grows without bound: the #4243 storm again,
  // reached from a different direction.
  private pending = new Set<string>();
  private pendingDisplay = new Set<string>();
  /** when the oldest still-unflushed date was collected — drives the hard cap */
  private firstPendingAt_ms: number | null = null;
  /** when the newest date was collected — drives the trailing debounce */
  private lastNoteAt_ms: number | null = null;
  /** the same two clocks, for the display list's own faster rhythm */
  private firstDisplayAt_ms: number | null = null;
  private lastDisplayNoteAt_ms: number | null = null;
  /** when the last bounded fallback was handed out, so the next one can be made
   *  to wait (#4406). Null means "no fallback is holding anything back", which is
   *  both the starting state and what a requeue restores. */
  private lastFallbackAt_ms: number | null = null;

  /**
   * Record the dates a batch of pulled docs affected.
   * Returns whether the caller should (re)arm its debounce timer.
   */
  noteDates(dateKeys: string[], nowMs: number): { armTimer: boolean } {
    if (dateKeys.length === 0) {
      return { armTimer: false };
    }
    for (const dateKey of dateKeys) {
      this.pending.add(dateKey);
      this.pendingDisplay.add(dateKey);
    }
    if (this.firstPendingAt_ms == null) {
      this.firstPendingAt_ms = nowMs;
    }
    this.lastNoteAt_ms = nowMs;
    if (this.firstDisplayAt_ms == null) {
      this.firstDisplayAt_ms = nowMs;
    }
    this.lastDisplayNoteAt_ms = nowMs;
    return { armTimer: true };
  }

  /**
   * Decide what to do with the pending dates.
   *
   * `force` skips the wait check — used when the catch-up sync completes, when
   * the app resumes with dates still pending, and by the hard-cap timer.
   * A `Hold` answer leaves the pending set untouched; `Flush` and
   * `FallbackRange` both consume it.
   */
  takeDecision(nowMs: number, initialSyncCompleted: boolean, force = false): ReactiveDayStatusDecision {
    if (this.pending.size === 0) {
      return EMPTY_DECISION;
    }

    // While the catch-up sync runs, nothing computed may be persisted (I1) — but
    // it can still be DISPLAYED. The persist list keeps accumulating for the
    // post-sync flush either way; what happens here is only about what the user
    // sees while they wait.
    if (!initialSyncCompleted) {
      // if nothing new has arrived since the last display tick, there is nothing
      // to recompute — but the persist list still needs its post-sync flush
      if (this.pendingDisplay.size === 0) {
        return { ...EMPTY_DECISION, action: ReactiveDayStatusAction.Hold };
      }
      // if the display window has not elapsed, wait — one replication batch
      // should produce one recompute, not one per document
      if (!force && !this.isReadyToFlushDisplay(nowMs)) {
        return { ...EMPTY_DECISION, action: ReactiveDayStatusAction.Hold };
      }
      const displayDates = [...this.pendingDisplay].sort();
      this.clearPendingDisplay();
      // NOTE: no volume fallback here, unlike the persisting path below. The
      // fallback DELETES days from the cache doc, which is a write — forbidden
      // mid-sync. Clustering already bounds the cost: contiguous days merge into
      // one local read, so a whole month of arriving days is one or two reads,
      // far cheaper than the view query this replaces.
      return {
        action: ReactiveDayStatusAction.DisplayOnly,
        clusters: clusterAffectedDates(displayDates),
        dates: displayDates,
        rangeStart: displayDates[0],
        rangeEnd: displayDates[displayDates.length - 1],
      };
    }

    if (!force && !this.isReadyToFlush(nowMs)) {
      return EMPTY_DECISION;
    }

    // If this would be a bounded fallback, it may have to wait for the one
    // before it (#4406). Checked HERE, before the sort and the clear below, for
    // two separate reasons: the dates must stay in the set, so returning before
    // `clearPending` is what makes them merge into the next fallback instead of
    // being lost; and the held set reaches hundreds of dates during a drain, so
    // sorting it on every refused tick would be the only real cost of refusing.
    //
    // `force` deliberately does NOT override this — the hard-cap timer forces
    // every two seconds, so honouring force here would mean no rate limit at
    // all. A forcing caller that is refused is NOT stuck: the service's `None`
    // branch re-arms its timers whenever dates are still pending, so the wave
    // goes out within a couple of seconds of the interval elapsing. That
    // re-arm is what makes refusing a forced call safe, so do not remove it.
    if (this.pending.size > REACTIVE_STATUS_MAX_DATES_PER_FLUSH && !this.isFallbackIntervalElapsed(nowMs)) {
      return EMPTY_DECISION;
    }

    const dates = [...this.pending].sort();
    this.clearPending();

    // a burst this large is a warm-resume sync, not a handful of cross-device
    // edits. recomputing each day would mean dozens of reads and cache writes,
    // so invalidate the span once and let the normal load path refill it.
    //
    // The clusters here are PUBLISHED, never read (#4451). This branch still
    // does no per-day recompute — the caller deletes the dates and publishes,
    // and it never iterates these. They are computed so the published event can
    // say WHERE inside `rangeStart..rangeEnd` the changed days actually are;
    // without them a drain wave that straddles a calendar's visible days forces
    // it to reload for days none of them shows.
    if (dates.length > REACTIVE_STATUS_MAX_DATES_PER_FLUSH) {
      this.lastFallbackAt_ms = nowMs;
      return {
        action: ReactiveDayStatusAction.FallbackRange,
        clusters: clusterAffectedDates(dates),
        dates,
        rangeStart: dates[0],
        rangeEnd: dates[dates.length - 1],
      };
    }

    return {
      action: ReactiveDayStatusAction.Flush,
      clusters: clusterAffectedDates(dates),
      dates,
      rangeStart: dates[0],
      rangeEnd: dates[dates.length - 1],
    };
  }

  /**
   * Put dates back after a flush could not be trusted: the read was unavailable
   * (offline, database recovering) or came back unverified, and per I3 an
   * unverified answer may be displayed but never persisted. The dates are
   * retried on the next hard-cap tick or the next pulled doc, whichever comes
   * first — never silently dropped.
   */
  requeue(dateKeys: string[], nowMs: number, requeuedAction: ReactiveDayStatusAction): void {
    if (dateKeys.length === 0) return;
    for (const dateKey of dateKeys) {
      this.pending.add(dateKey);
    }
    // restart the clock so the retry is bounded by the same hard cap
    this.firstPendingAt_ms = nowMs;
    this.lastNoteAt_ms = nowMs;
    // if it was a FALLBACK that failed, forget its timestamp (#4406). The stamp
    // is written when a fallback decision is handed out, but a requeue says that
    // decision never took effect — nothing was deleted and nothing was
    // published — so making its retry wait out the full interval would be
    // throttling work that never happened, on top of the caller's own failure
    // back-off.
    //
    // `requeuedAction` is why this takes an argument rather than clearing
    // unconditionally. The other caller is the ordinary Flush path, whose
    // unverified dates are requeued as expected noise while a view index lags a
    // drain. Clearing on THAT would wipe a stamp belonging to a fallback that
    // really did delete and publish, and the next over-the-bound wave would fire
    // a second fallback seconds after the first — the exact churn this rate
    // limit exists to stop.
    if (requeuedAction === ReactiveDayStatusAction.FallbackRange) {
      this.lastFallbackAt_ms = null;
    }
  }

  /**
   * Drop everything. Called when the user database is initialized or switched,
   * so a pending date from one user can never trigger a recompute against
   * another user's database, and at teardown so no timer outlives its database.
   */
  reset(): void {
    this.clearPending();
    // a different database's fallback must not hold this one's first fallback
    // back — the new database's calendar has shown nothing yet (#4406)
    this.lastFallbackAt_ms = null;
  }

  hasPending(): boolean {
    return this.pending.size > 0;
  }

  /**
   * True when enough time has passed since the last bounded fallback for another
   * one to run (#4406). Always true when none has run yet.
   */
  private isFallbackIntervalElapsed(nowMs: number): boolean {
    if (this.lastFallbackAt_ms == null) return true;
    // if the wall clock moved BACKWARDS (NTP correction, manual change, device
    // resume), the stored stamp now sits in the future and the subtraction below
    // could not reach the interval for as long as the jump lasts — which would
    // hold every fallback back indefinitely. A stamp in the future is meaningless,
    // so treat it as elapsed. Same hazard the hard-cap timer forces past.
    if (nowMs < this.lastFallbackAt_ms) return true;
    return nowMs - this.lastFallbackAt_ms >= REACTIVE_STATUS_MIN_FALLBACK_INTERVAL_MS;
  }

  /** True once either the trailing debounce or the hard cap has elapsed. */
  private isReadyToFlush(nowMs: number): boolean {
    if (this.pending.size === 0) return false;
    const debounceElapsed = this.lastNoteAt_ms != null && nowMs - this.lastNoteAt_ms >= REACTIVE_STATUS_DEBOUNCE_MS;
    const hardCapElapsed = this.firstPendingAt_ms != null && nowMs - this.firstPendingAt_ms >= REACTIVE_STATUS_HARD_CAP_MS;
    return debounceElapsed || hardCapElapsed;
  }

  /** The same test for the display list, on its own faster catch-up clocks. */
  private isReadyToFlushDisplay(nowMs: number): boolean {
    if (this.pendingDisplay.size === 0) return false;
    const debounceElapsed = this.lastDisplayNoteAt_ms != null
      && nowMs - this.lastDisplayNoteAt_ms >= REACTIVE_STATUS_CATCHUP_DEBOUNCE_MS;
    const hardCapElapsed = this.firstDisplayAt_ms != null
      && nowMs - this.firstDisplayAt_ms >= REACTIVE_STATUS_CATCHUP_HARD_CAP_MS;
    return debounceElapsed || hardCapElapsed;
  }

  private clearPending(): void {
    this.pending.clear();
    this.firstPendingAt_ms = null;
    this.lastNoteAt_ms = null;
    // the persist list draining also ends the display round it belonged to: the
    // memo is superseded by what was just persisted
    this.clearPendingDisplay();
  }

  private clearPendingDisplay(): void {
    this.pendingDisplay.clear();
    this.firstDisplayAt_ms = null;
    this.lastDisplayNoteAt_ms = null;
  }
}

/** Registers a listener for initial-sync outcomes; returns an unsubscribe. */
export type InitialSyncSettledSubscribe = (
  listener: (completed: boolean) => void,
) => () => void;

/**
 * Run `onSucceeded` the first time the initial sync SUCCEEDS, however many
 * rounds that takes (#4435).
 *
 * The distinction that matters is SUCCEEDS versus SETTLES. The initial sync can
 * settle several times before it works: each round the retry loop runs reports an
 * outcome, and a device that is offline reports failure outright. A caller
 * waiting for "the sync is done, I may write now" must sit through every failed
 * round and keep waiting — a failed round is not an answer to its question.
 *
 * Which is why this takes a SUBSCRIPTION rather than a promise. A promise settles
 * once and can only carry the first round's answer; the caller that awaited it is
 * then holding something that will never speak again, and the round that finally
 * succeeds tells it nothing.
 *
 * Nothing here polls or retries. While the sync keeps failing this waits, at no
 * cost, and the wait ends when a real outcome arrives.
 */
export function runOnInitialSyncSuccess(
  subscribe: InitialSyncSettledSubscribe,
  onSucceeded: () => void,
): () => void {
  let unsubscribe: (() => void) | null = null;
  let done = false;

  const stop = () => {
    done = true;
    if (unsubscribe != null) {
      const handle = unsubscribe;
      unsubscribe = null;
      handle();
    }
  };

  unsubscribe = subscribe(completed => {
    if (done) return;
    // a round that gave up is not what we are waiting for — keep listening
    if (!completed) return;
    stop();
    onSucceeded();
  });

  // the outcome can arrive during `subscribe` itself, before it has returned the
  // handle. `stop` could not use a handle it did not have yet, so run it again
  // now that we do — otherwise a spent waiter stays subscribed forever.
  if (done) stop();
  return stop;
}

/** Which initial-sync outcome a waiter is willing to be woken by. */
export enum InitialSyncWaitFor {
  /** only a sync that worked; a round that gave up is not an answer, so keep waiting */
  Success = 'Success',
  /** any terminal outcome, success or failure — "the sync has stopped trying for now" */
  AnyOutcome = 'AnyOutcome',
}

/** How `waitForInitialSyncAsync` ended. */
export enum InitialSyncWaitResult {
  /** the initial sync reported success */
  Succeeded = 'Succeeded',
  /** the initial sync reported failure, and the caller asked to be told about that too */
  Failed = 'Failed',
  /** no outcome arrived before the cap ran out — the caller carries on with local data */
  GaveUpWaiting = 'GaveUpWaiting',
  /** reads already went to this device's own store, so there was nothing to wait for
   *  (only `waitForReadsToGoLocalAsync` answers this) */
  ReadsAlreadyLocal = 'ReadsAlreadyLocal',
}

/**
 * How long a caller outside `DatabaseWrapper` waits for the initial sync before
 * carrying on without it. The same figure the wrapper's own `awaitSyncWithTimeout`
 * allows a single sync round, chosen so a waiter and a healthy round agree about
 * how long "a while" is.
 */
export const INITIAL_SYNC_WAIT_CAP_MS = 60_000;

/**
 * Wait for the initial sync, and always stop waiting eventually.
 *
 * Callers outside `DatabaseWrapper` used to `await database.whenInitialSyncCompleted`
 * instead, and that has two separate ways of never answering:
 *
 *  - Awaiting the property captures THAT promise object, while the retry loops
 *    replace the field with a fresh one per round. A caller suspended on the old
 *    object is never resolved by anything (#4435).
 *  - Some paths reach no terminal outcome at all. When the retry loop exhausts its
 *    attempts while the device is online it deliberately leaves the sync unsettled,
 *    so that reads keep passing through to remote and a later resume can retry — and
 *    a cancelled initial sync returns without resolving anything either.
 *
 * Subscribing fixes the first, because the subscription fires on every round. The
 * cap covers the second: when nothing ever answers, the caller is released with
 * `GaveUpWaiting` and proceeds on whatever data is already local.
 *
 * Never throws, always unsubscribes, and always clears its timer.
 */
export function waitForInitialSyncAsync(
  subscribe: InitialSyncSettledSubscribe,
  waitFor: InitialSyncWaitFor,
  capMs: number = INITIAL_SYNC_WAIT_CAP_MS,
): Promise<InitialSyncWaitResult> {
  // while waiting for a success, a round that gave up is not an answer to the
  // question being asked — keep listening for the next round
  return waitForInitialSyncOutcomeAsync(
    subscribe,
    completed => completed || waitFor === InitialSyncWaitFor.AnyOutcome,
    capMs,
  );
}

/**
 * Wait until a read of this database goes to the device's own store rather than the
 * server, and always stop waiting eventually (df-5c87afe2dd40).
 *
 * "The initial sync settled" is not that moment. Since #6554 a round that fails while
 * the device is ONLINE leaves the read gate open, so reads keep going to the server
 * while the retry loop waits. Only a round that succeeded, or a failed round that
 * closed the gate (the device was offline), moves reads to the local store. So a
 * failed round is an answer here only when `readsPassThroughToServer` says the reads
 * have moved; otherwise this keeps listening for the next round, up to the cap.
 *
 * If reads already go local when this is called, it answers at once with
 * `ReadsAlreadyLocal`. Never throws, always unsubscribes, and always clears its timer.
 */
export function waitForReadsToGoLocalAsync(
  subscribe: InitialSyncSettledSubscribe,
  readsPassThroughToServer: () => boolean,
  capMs: number = INITIAL_SYNC_WAIT_CAP_MS,
): Promise<InitialSyncWaitResult> {
  // if nothing goes to the server now, there is nothing to wait for
  if (!readsPassThroughToServer()) return Promise.resolve(InitialSyncWaitResult.ReadsAlreadyLocal);
  return waitForInitialSyncOutcomeAsync(
    subscribe,
    completed => completed || !readsPassThroughToServer(),
    capMs,
  );
}

// the shared body of the two capped waits above: `isAnswer` decides whether one
// round's outcome ends the wait
function waitForInitialSyncOutcomeAsync(
  subscribe: InitialSyncSettledSubscribe,
  isAnswer: (completed: boolean) => boolean,
  capMs: number,
): Promise<InitialSyncWaitResult> {
  return new Promise<InitialSyncWaitResult>(resolve => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = (result: InitialSyncWaitResult) => {
      if (settled) return;
      settled = true;
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      if (unsubscribe != null) {
        const handle = unsubscribe;
        unsubscribe = null;
        handle();
      }
      resolve(result);
    };

    const handle = subscribe(completed => {
      // if this round is not what the caller is waiting for, keep listening
      if (!isAnswer(completed)) return;
      finish(completed ? InitialSyncWaitResult.Succeeded : InitialSyncWaitResult.Failed);
    });

    // the outcome can arrive during `subscribe` itself, before it has returned the
    // handle — `finish` had no handle to release, so release it here and start no
    // timer, otherwise a spent waiter stays subscribed forever
    if (settled) {
      handle();
      return;
    }
    unsubscribe = handle;
    timer = setTimeout(() => finish(InitialSyncWaitResult.GaveUpWaiting), capMs);
  });
}

/**
 * Why a reactive flush gave up part-way (df-44348d1531e4). The string values are
 * the exact words the display refresh writes into its tick log line, so they
 * must not be reworded casually — a log reader searches for them.
 */
export enum ReactiveFlushAbandonReason {
  /** The user database was switched: every date the flush holds belongs to the previous user. */
  DatabaseSwitched = 'database switched',
  /** A save during the catch-up sync evicted memo days, so a display recompute started before it is stale. */
  MemoInvalidated = 'memo invalidated',
}

/**
 * Thrown by a `ReactiveFlushToken` when a counter it watches has moved.
 *
 * This is NOT a failure. It is how a flush stops at the first await after a
 * database switch without writing, publishing or requeueing anything, and every
 * catch it can reach must treat it as expected: never `console.error`, never a
 * Sentry report. Test for it with `isReactiveFlushAbandoned`.
 */
export class ReactiveFlushAbandoned extends Error {
  constructor(readonly reason: ReactiveFlushAbandonReason) {
    super(`Reactive flush abandoned: ${reason}`);
    this.name = 'ReactiveFlushAbandoned';
  }
}

export function isReactiveFlushAbandoned(err: unknown): err is ReactiveFlushAbandoned {
  return err instanceof ReactiveFlushAbandoned;
}

/** One counter a flush token watches: its current value, and what to report if it moves. */
export interface ReactiveFlushWatchedCounter {
  reason: ReactiveFlushAbandonReason;
  read: () => number;
}

/**
 * The cross-user-write guard for one in-flight reactive flush, in one place
 * (df-44348d1531e4; the rule itself is df-1661467a4dee).
 *
 * `DayTaskStatusService` bumps a generation counter whenever the user database
 * is switched, and a memo epoch whenever display-only memo days are evicted. A
 * flush that is paused at an await when either moves is holding stale dates and
 * must give up. This token reads the counters it is given ONCE, when it is
 * created, and compares them again whenever it is asked to.
 *
 * - `run(work)` awaits the work, then checks. Its result is only handed back if
 *   nothing moved, so a caller can never act on a read that crossed a switch.
 * - `exclusiveCall(mutex, work)` also checks once the lock is taken, before the
 *   work starts — the wait for the lock is not an await inside the caller, so
 *   `run` cannot see it — and once more after the lock is released.
 * - `check()` compares without awaiting anything, for a place that must decide
 *   between two awaits.
 *
 * When a counter has moved it throws `ReactiveFlushAbandoned`, naming the FIRST
 * moved counter in the order they were given. The counters are listed with the
 * database switch first on purpose: when both move, a switch is what happened.
 *
 * A flush that needs a side effect gated on the counter NOT having moved (a
 * requeue in a catch, a re-arm in a finally) keeps its own comparison: a throw
 * cannot express "do this only if still current". See runReactiveStatusFlushAsync.
 */
export class ReactiveFlushToken {
  private readonly watched: { reason: ReactiveFlushAbandonReason; read: () => number; captured: number }[];

  constructor(counters: ReactiveFlushWatchedCounter[]) {
    this.watched = counters.map(counter => ({ ...counter, captured: counter.read() }));
  }

  /** Throw `ReactiveFlushAbandoned` if any watched counter has moved since this token was created. */
  check(): void {
    for (const counter of this.watched) {
      if (counter.read() !== counter.captured) {
        throw new ReactiveFlushAbandoned(counter.reason);
      }
    }
  }

  /** Await the work, then check. A rejected work's own error passes through unchanged. */
  async run<T>(work: () => Promise<T>): Promise<T> {
    const result = await work();
    this.check();
    return result;
  }

  /** Run the work under the mutex, checking once the lock is held and again after it is released. */
  async exclusiveCall<T>(mutex: Mutex, work: () => Promise<T>): Promise<T> {
    const result = await mutex.exclusiveCall(() => {
      this.check();
      return work();
    });
    this.check();
    return result;
  }
}
